import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { TagChipInput } from "@/components/admin/TagChipInput";
import { CharacterQuickTags } from "@/components/admin/CharacterQuickTags";

interface EditableImage {
  id: string;
  original_name: string;
  tags: string[];
  audience: string[];
}

interface Props {
  image: EditableImage | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSaved: () => void;
}

export function EditImageDialog({ image, open, onOpenChange, onSaved }: Props) {
  const [name, setName] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [audience, setAudience] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (image) {
      setName(image.original_name);
      setTags(image.tags);
      setAudience(image.audience);
    }
  }, [image]);

  const save = async () => {
    if (!image) return;
    setSaving(true);
    const { error } = await supabase
      .from("images")
      .update({
        original_name: name.trim() || image.original_name,
        tags,
        audience,
      })
      .eq("id", image.id);
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success("已更新");
    onOpenChange(false);
    onSaved();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>編輯圖片資訊</DialogTitle>
          <DialogDescription>更新搜尋名稱、標籤與適用對象。</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>搜尋名稱</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>標籤</Label>
            <CharacterQuickTags tags={tags} onChange={setTags} />
            <TagChipInput tags={tags} onChange={setTags} placeholder="輸入標籤後按 Enter" />
          </div>
          <div className="space-y-2">
            <Label>適用對象</Label>
            <div className="flex gap-4 pt-1">
              {[
                { key: "parents", label: "家長" },
                { key: "teachers", label: "老師" },
              ].map((a) => (
                <label key={a.key} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={audience.includes(a.key)}
                    onCheckedChange={(v) =>
                      setAudience((prev) =>
                        v
                          ? Array.from(new Set([...prev, a.key]))
                          : prev.filter((x) => x !== a.key)
                      )
                    }
                  />
                  {a.label}
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving ? "儲存中…" : "儲存"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
